import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import CPFMaskInput from '../masks/CPFMaskInput';
import { unformatCPF, formatCPF, isValidCPF } from '../../utils/cpfUtils';

const UsuarioModal = ({ isOpen, usuario, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    cpf: '',
    nome: '',
    login: '',
    senha: '',
    situacao: '',
    role: '',
    cargo: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const token = localStorage.getItem('token');

  // Atualiza formData ao abrir modal ou mudar usuário
  useEffect(() => {
    if (isOpen) {
      if (usuario) {
        setFormData({
          cpf: usuario.cpf || '',
          nome: usuario.nome || '',
          login: usuario.login || '',
          senha: usuario.senha || '',
          situacao: usuario.situacao || '',
          role: usuario.role || '',
          cargo: usuario.cargo || '',
          email: usuario.email || '',
        });
        setIsEditing(true);
      } else {
        // Limpa completamente o formulário para novo usuário
        setFormData({
          cpf: '',
          nome: '',
          login: '',
          senha: '',
          situacao: '',
          role: '',
          cargo: '',
          email: '',
        });
        setIsEditing(false);
      }
      setError(null);
      setEmailError('');
    }
  }, [isOpen, usuario]);

  const handleInputChange = useCallback(event => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));

    // Validação de e-mail em tempo real
    if (name === 'email') {
      validateEmail(value);
    }
  }, []);

  const validateEmail = email => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('');
    } else if (!re.test(email)) {
      setEmailError('E-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const validateForm = useCallback(() => {
    if (!formData.cpf || !isValidCPF(formData.cpf)) {
      setError('CPF é obrigatório e deve ser válido.');
      return false;
    }
    if (!formData.nome.trim()) {
      setError('Nome é obrigatório.');
      return false;
    }
    if (!formData.login.trim()) {
      setError('Login é obrigatório.');
      return false;
    }
    if (!isEditing && !formData.senha.trim()) {
      setError('Senha é obrigatória para novos usuários.');
      return false;
    }
    if (!formData.situacao) {
      setError('Situação é obrigatória.');
      return false;
    }
    if (!formData.role) {
      setError('Tipo de usuário é obrigatório.');
      return false;
    }
    if (formData.email && emailError) {
      setError('Por favor, corrija o e-mail antes de salvar.');
      return false;
    }
    return true;
  }, [formData, isEditing, emailError]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        const dataToSend = {
          ...formData,
          cpf: unformatCPF(formData.cpf),
        };

        await axios.post(`${API_BASE_URL.USUARIO}/salvar`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (onSuccess) {
          onSuccess(formData, isEditing);
        }
        onClose();
      } catch (error) {
        console.error('Erro ao salvar usuário:', error);
        setError(
          error.response?.data?.message ||
            'Erro ao salvar usuário. Tente novamente.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validateForm, token, onSuccess, onClose, isEditing]
  );

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <>
      <Modal show={isOpen} onHide={handleClose} size="lg" backdrop="static">
        <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
          <h6 className="modal-title text-center m-0">
            <i
              className={`bi ${
                isEditing ? 'bi-person-gear' : 'bi-person-plus'
              } me-2`}
            ></i>
            {isEditing ? 'Editar Usuário' : 'Novo Usuário'}
          </h6>
          <button
            className="btn btn-link text-white p-0 mb-0"
            onClick={handleClose}
            title="Fechar"
            style={{ fontSize: '0.9rem', textDecoration: 'none' }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Form onSubmit={handleSubmit} key={usuario?.cpf || 'new'}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Row className="g-3">
              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>CPF *</Form.Label>
                  <CPFMaskInput
                    value={formatCPF(formData.cpf)}
                    onChange={e =>
                      handleInputChange({
                        target: {
                          name: 'cpf',
                          value: unformatCPF(e.target.value),
                        },
                      })
                    }
                    id="cpf"
                    name="cpf"
                    type="text"
                    className="form-control"
                    size="sm"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group className="mb-1">
                  <Form.Label>Nome Completo *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    size="sm"
                    required
                    maxLength={100}
                    placeholder="Nome completo do usuário"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>Login *</Form.Label>
                  <Form.Control
                    type="text"
                    name="login"
                    value={formData.login}
                    onChange={handleInputChange}
                    size="sm"
                    required
                    maxLength={50}
                    placeholder="Login de acesso"
                    autoComplete="off"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>
                    Senha *
                    {isEditing && (
                      <small className="text-muted">
                        {' '}
                        (altere apenas se necessário)
                      </small>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="password"
                    name="senha"
                    value={formData.senha}
                    onChange={handleInputChange}
                    size="sm"
                    required
                    placeholder={
                      isEditing ? 'Senha atual ou nova senha' : 'Senha'
                    }
                    autoComplete="new-password"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-1">
                  <Form.Label>Situação *</Form.Label>
                  <Form.Select
                    name="situacao"
                    value={formData.situacao}
                    onChange={handleInputChange}
                    size="sm"
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="ATIVO">ATIVO</option>
                    <option value="INATIVO">INATIVO</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-1">
                  <Form.Label>Tipo de Usuário *</Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    size="sm"
                    required
                  >
                    <option value="">Selecione...</option>
                    <option value="ADMIN">ADMINISTRADOR</option>
                    <option value="USER">USUÁRIO</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-1">
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    size="sm"
                    className={emailError ? 'is-invalid' : ''}
                    placeholder="usuario@exemplo.com"
                  />
                  {emailError && (
                    <div className="invalid-feedback">{emailError}</div>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group className="mb-1">
                  <Form.Label>Cargo / Função</Form.Label>
                  <Form.Control
                    type="text"
                    name="cargo"
                    value={formData.cargo}
                    onChange={handleInputChange}
                    size="sm"
                    maxLength={100}
                    placeholder="Ex: Analista, Coordenador, Gerente..."
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-end gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Salvando...
                </>
              ) : isEditing ? (
                'Atualizar'
              ) : (
                'Salvar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

UsuarioModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  usuario: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default UsuarioModal;
